import { isHeyNowMobilePlatformContext } from './heynow-chat-ios-native.util';
import {
  applyHeyNowLauncherPosition,
  clampHeyNowLauncherToViewport,
  holdHeyNowLauncherPosition,
  HeyNowLauncherPosition,
  readHeyNowLauncherPosition,
  saveHeyNowLauncherPosition,
} from './heynow-chat-position.util';

export const HEYNOW_IOS_DRAGGING_CLASS = 'bqm-heynow-dragging';

const DRAG_THRESHOLD_PX = 10;
const CLICK_SUPPRESS_MS = 300;
const VENDOR_LAYOUT_INTERVAL_MS = 400;

/** Mobile platforms (native Capacitor + mobile web): vendor drag is mouse-only; pointer handlers move the collapsed launcher. */
export function attachHeyNowIosLauncherTouchDrag(chatRoot: HTMLElement, storage: Storage = localStorage): () => void {
  if (!isHeyNowMobilePlatformContext()) {
    return () => undefined;
  }

  let pointerId: number | null = null;
  let startX = 0;
  let startY = 0;
  let startTop = 0;
  let startLeft = 0;
  let dragging = false;
  let suppressClickUntil = 0;
  let lastDragPosition: HeyNowLauncherPosition | null = null;

  const launcherSize = (): { width: number; height: number } => {
    const rect = chatRoot.getBoundingClientRect();
    return { width: rect.width || 56, height: rect.height || 56 };
  };

  const onPointerDown = (event: PointerEvent): void => {
    if (chatRoot.classList.contains('hey-expanded') || pointerId !== null) {
      return;
    }
    pointerId = event.pointerId;
    startX = event.clientX;
    startY = event.clientY;
    const saved = readHeyNowLauncherPosition(storage);
    if (saved) {
      startTop = saved.top;
      startLeft = saved.left;
    } else {
      const rect = chatRoot.getBoundingClientRect();
      startTop = rect.top;
      startLeft = rect.left;
    }
    dragging = false;
    lastDragPosition = null;
    chatRoot.setPointerCapture(event.pointerId);
  };

  const onPointerMove = (event: PointerEvent): void => {
    if (pointerId !== event.pointerId || chatRoot.classList.contains('hey-expanded')) {
      return;
    }
    const dx = event.clientX - startX;
    const dy = event.clientY - startY;
    if (!dragging && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) {
      return;
    }
    dragging = true;
    event.preventDefault();
    chatRoot.classList.add(HEYNOW_IOS_DRAGGING_CLASS);
    lastDragPosition = clampHeyNowLauncherToViewport({ top: startTop + dy, left: startLeft + dx }, launcherSize());
    applyHeyNowLauncherPosition(chatRoot, lastDragPosition);
  };

  const endDrag = (event: PointerEvent): void => {
    if (pointerId !== event.pointerId) {
      return;
    }
    if (chatRoot.hasPointerCapture(event.pointerId)) {
      chatRoot.releasePointerCapture(event.pointerId);
    }
    pointerId = null;
    if (dragging && lastDragPosition) {
      chatRoot.classList.remove(HEYNOW_IOS_DRAGGING_CLASS);
      applyHeyNowLauncherPosition(chatRoot, lastDragPosition);
      saveHeyNowLauncherPosition(lastDragPosition, storage);
      suppressClickUntil = Date.now() + CLICK_SUPPRESS_MS;
    }
    dragging = false;
    lastDragPosition = null;
  };

  const onClickCapture = (event: Event): void => {
    if (Date.now() < suppressClickUntil) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  chatRoot.addEventListener('pointerdown', onPointerDown);
  chatRoot.addEventListener('pointermove', onPointerMove, { passive: false });
  chatRoot.addEventListener('pointerup', endDrag);
  chatRoot.addEventListener('pointercancel', endDrag);
  chatRoot.addEventListener('click', onClickCapture, true);

  const layoutInterval = globalThis.setInterval(() => {
    holdHeyNowLauncherPosition(chatRoot, storage);
  }, VENDOR_LAYOUT_INTERVAL_MS);

  return () => {
    globalThis.clearInterval(layoutInterval);
    chatRoot.removeEventListener('pointerdown', onPointerDown);
    chatRoot.removeEventListener('pointermove', onPointerMove);
    chatRoot.removeEventListener('pointerup', endDrag);
    chatRoot.removeEventListener('pointercancel', endDrag);
    chatRoot.removeEventListener('click', onClickCapture, true);
    chatRoot.classList.remove(HEYNOW_IOS_DRAGGING_CLASS);
  };
}
